import { useEffect, useRef, useState } from 'react';
import { Drawer } from 'antd';
import { SuggestionCard, SwitchButton } from 'modules/v2/common/AtomicDesign/atoms';
import { Notification, WarnMessage } from 'modules/v2/common/AtomicDesign/molecules';
import { ProcessingModal } from 'modules/v2/common/AtomicDesign/organisms';
import { DangerTriangleIcon, LockIcon } from 'modules/v2/common/components/SvgIcon';
import RelativeLoading from '../../../RelativeLoading';
import { customLoadingModalTheme } from './theme';

interface AddCardModalProps {
  showModal: boolean;
  setShowModal: (showModal: boolean) => void;
  deleteDefaultCard?: boolean;
  customerEmail?: string;
}

const AddCardModal = ({
  showModal,
  setShowModal,
  deleteDefaultCard = false,
  customerEmail,
}: AddCardModalProps) => {
  const compRef = useRef<any>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [shimLoading, setShimLoading] = useState(false);
  const [errorNotification, setErrorNotification] = useState<string | null>(null);
  const [successNotification, setSuccessNotification] = useState<string | null>(null);
  const [checked, setChecked] = useState(true); // cosmetic without backend

  useEffect(() => {
    setShimLoading(true);
    const t = setTimeout(() => setShimLoading(false), 600);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!successNotification) return;
    const t = setTimeout(() => setSuccessNotification(null), 5000);
    return () => clearTimeout(t);
  }, [successNotification]);

  const handlePayment = async () => {
    setErrorNotification(null);
    try {
      setIsLoading(true);
      setSuccessNotification('Payment method added successfully.');
      setShowModal(false);
    } catch (e) {
      console.error(e);
      setErrorNotification(e?.message || 'Could not add payment method.');
    } finally {
      setIsLoading(false);
    }
  };

  const footerDrawer = (
    <div
      className="bg-primary-500 text-neutral-800 font-bold px-[18px] py-[13px] gap-2 rounded-md h-11 w-full flex justify-center items-center cursor-pointer"
      onClick={handlePayment}
    >
      Add new payment
    </div>
  );

  return (
    <Drawer
      title="Add new payment method"
      open={showModal}
      onClose={() => setShowModal(false)}
      footer={footerDrawer}
    >
      <div className="w-[440-px] mb-4">
        {deleteDefaultCard && (
          <WarnMessage type="error">
            <div className="flex gap-3">
              <div className="w-4 h-4">
                <DangerTriangleIcon width={20} height={20} />
              </div>
              <div>
                This card is used as your default payment. To delete this card, add a new one.
              </div>
            </div>
          </WarnMessage>
        )}
        <SuggestionCard
          icon={<LockIcon fill="#069668" />}
          description="Your card details are encrypted and handled securely by Chargebee. We never store raw card numbers."
          className=""
        />
      </div>

      <div className="mb-6">
        <RelativeLoading isLoading={shimLoading} />
        <div className="space-y-3"></div>
      </div>

      <div className="flex items-center gap-2 font-semibold text-sm mb-12">
        <SwitchButton checked={checked} setChecked={setChecked} />
        <span>Set as my default payment</span>
      </div>

      <ProcessingModal
        showModal={isLoading}
        title=""
        description="Adding new payment method..."
        theme={customLoadingModalTheme}
      />

      {errorNotification && <Notification type="error" description={errorNotification} />}
      {successNotification && <Notification type="success" description={successNotification} />}
    </Drawer>
  );
};

export default AddCardModal;
